"use client";

import { useState, useRef, useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import { Tooltip } from "./Tooltip";
import { Typography } from "./Typography";
import { cn } from "@/lib/utils/cn";

export interface TruncatedFieldProps {
  label: string;
  value?: string;
  /** Shows a red warning icon in a tooltip when there is no value. */
  required?: boolean;
  onClick?: () => void;
}

/**
 * A labelled read-only field that clamps to three lines and reveals the full text
 * in a tooltip only when it is actually clipped.
 *
 * Extracted from `LocationAccordion`, where it lived as a second component in the
 * same file (repo rule: one component per file). Behaviour is unchanged.
 *
 * The clamp detection has to be measured rather than computed: `line-clamp-3` is
 * pure CSS, so the only way to know whether the text overflowed is to compare
 * `scrollHeight` against `clientHeight` after layout. That is why the tooltip is
 * conditional on state rather than on the string length — a long word and a long
 * sentence clip at very different character counts.
 */
export function TruncatedField({
  label,
  value,
  required,
  onClick,
}: TruncatedFieldProps) {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsClamped(el.scrollHeight > el.clientHeight);
    }
  }, [value]);

  const text = value ? (
    <p
      ref={textRef}
      className={cn(
        "text-[14px] leading-normal text-gray-900 mt-0.5 line-clamp-3",
        isClamped && "cursor-help",
        onClick && "cursor-pointer hover:text-blue-600 transition-colors",
      )}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") onClick();
            }
          : undefined
      }
    >
      {value}
    </p>
  ) : null;

  return (
    <div>
      <span className="inline-flex items-center gap-1">
        <Typography as="span" variant="small" weight="medium" color="light">
          {label}
        </Typography>
        {!value && required && (
          <Tooltip content={`${label} is required`} placement="top">
            <AlertTriangle className="w-3 h-3 text-red-500 shrink-0" />
          </Tooltip>
        )}
      </span>
      {text &&
        (isClamped ? (
          <Tooltip content={value!} placement="bottom" className="block">
            {text}
          </Tooltip>
        ) : (
          text
        ))}
    </div>
  );
}
